import { useState, useEffect, useCallback } from "react";
import type { KitRecord, KitListResponse } from "../types/orders";

type FetchFn = (search: string, page: number, pageLimit: number) => Promise<KitListResponse>;

const PAGE_SIZE = 10;

export function useKitList(fetchFn: FetchFn) {
  const [items, setItems] = useState<KitRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (s: string, p: number) => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFn(s, p, PAGE_SIZE);
        setItems(data.items);
        setTotal(data.total);
      } catch {
        setError("Something went wrong. Please refresh the page.");
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [fetchFn],
  );

  useEffect(() => {
    load(appliedSearch, page);
  }, [load, appliedSearch, page]);

  const handleSearch = useCallback(() => {
    setPage(1);
    setAppliedSearch(search);
  }, [search]);

  const handleClearSearch = useCallback(() => {
    setSearch("");
    setPage(1);
    setAppliedSearch("");
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = useCallback(() => {
    load(appliedSearch, page);
  }, [load, appliedSearch, page]);

  const updateItem = useCallback((recordId: string, updater: (item: KitRecord) => KitRecord) => {
    setItems((prev) => prev.map((it) => (it.record_id === recordId ? updater(it) : it)));
  }, []);

  return {
    items,
    total,
    page,
    setPage,
    totalPages,
    search,
    setSearch,
    handleSearch,
    handleClearSearch,
    loading,
    error,
    refresh,
    updateItem,
  };
}
